import React, { useState, useMemo } from 'react';
import { infiniteScrollUtils } from '../../../config/infiniteScrollConfig';

const DateFilters = ({ 
  onSearch, 
  onReset, 
  isSearching, 
  loading,
  error,
  searchVentaId,
  setSearchVentaId,
  onSearchById,
  onClearVentaId
}) => {
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');

  // Memoizar datos de fechas para evitar recálculos
  const fechasFormateadas = useMemo(() => {
    const formatDate = (dateString) => {
      try {
        return new Date(dateString).toLocaleDateString('es-AR');
      } catch {
        return dateString;
      }
    };

    return {
      inicio: fechaInicio ? formatDate(fechaInicio) : null,
      fin: fechaFin ? formatDate(fechaFin) : null
    };
  }, [fechaInicio, fechaFin]);

  const handleSearch = () => {
    onSearch(fechaInicio, fechaFin);
  };

  const handleReset = () => {
    setFechaInicio('');
    setFechaFin('');
    onReset();
  };

  // Validación en tiempo real
  const validation = useMemo(() => {
    if (!fechaInicio || !fechaFin) return { valid: true };
    
    if (new Date(fechaInicio) > new Date(fechaFin)) {
      return { valid: false, error: 'La fecha de inicio no puede ser mayor que la fecha de fin.' };
    }
    
    return infiniteScrollUtils.validateDateRange(fechaInicio, fechaFin);
  }, [fechaInicio, fechaFin]);

  return (
    <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-4 border border-gray-200">
      <div className="flex items-center gap-2 mb-4">
        <div className="bg-gradient-to-r from-blue-500 to-indigo-500 p-2 rounded-lg">
          <span className="text-white text-lg">📅</span>
        </div>
        <div>
          <h2 className="text-lg font-bold text-gray-800">Filtrar por Fechas</h2>
          <p className="text-sm text-gray-600">Busca ventas en un rango de fechas específico</p>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Fecha de Inicio
          </label>
          <input
            type="date"
            value={fechaInicio}
            onChange={(e) => setFechaInicio(e.target.value)}
            className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white hover:border-blue-300"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Fecha de Fin
          </label>
          <input
            type="date"
            value={fechaFin}
            onChange={(e) => setFechaFin(e.target.value)}
            className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white hover:border-blue-300"
          />
        </div>

        <div className="flex items-end">
          <button
            onClick={handleSearch}
            disabled={isSearching || loading || !fechaInicio || !fechaFin || !validation.valid}
            className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed text-white font-bold py-2 px-4 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105 disabled:transform-none flex items-center justify-center gap-2"
          >
            {isSearching ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Buscando...
              </>
            ) : (
              <>
                <span className="text-lg">🔍</span>
                Buscar
              </>
            )}
          </button>
        </div>

        <div className="flex items-end">
          <button
            onClick={handleReset}
            disabled={loading || isSearching}
            className="w-full bg-gradient-to-r from-gray-400 to-gray-500 hover:from-gray-500 hover:to-gray-600 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed text-white font-bold py-2 px-4 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105 disabled:transform-none flex items-center justify-center gap-2"
          >
            <span className="text-lg">🔄</span>
            Limpiar
          </button>
        </div>
      </div>

      {/* Indicador de filtro activo */}
      {(fechaInicio || fechaFin) && (
        <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-blue-600 text-lg">🔍</span>
            <p className="text-sm text-blue-700">
              <span className="font-medium">Filtro activo:</span>
              {fechaInicio && fechaFin ? (
                ` Desde ${fechasFormateadas.inicio} hasta ${fechasFormateadas.fin}`
              ) : fechaInicio ? (
                ` Desde ${fechasFormateadas.inicio}`
              ) : (
                ` Hasta ${fechasFormateadas.fin}`
              )}
            </p>
          </div>
          <button
            onClick={handleReset}
            disabled={loading || isSearching}
            className="text-blue-600 hover:text-blue-800 bg-blue-100 hover:bg-blue-200 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed rounded-full w-6 h-6 flex items-center justify-center transition-all duration-200 text-sm font-bold"
            title="Quitar filtro"
          >
            ✕
          </button>
        </div>
      )}

      {/* Filtro por número de venta (ID) en zona de consejos */}
      {!fechaInicio && !fechaFin && !loading && (
        <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200 flex flex-col md:flex-row md:items-end md:gap-4">
          <form onSubmit={onSearchById} className="flex gap-2 items-end">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Buscar por N° de Venta (ID)</label>
              <input
                type="number"
                min="1"
                value={searchVentaId}
                onChange={e => setSearchVentaId(e.target.value)}
                placeholder="Ej: 1234"
                className="border-2 border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white hover:border-blue-300 w-40"
              />
            </div>
            <button
              type="submit"
              className="bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white font-bold py-2 px-4 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105"
              disabled={loading || isSearching || !searchVentaId.trim()}
            >
              <span className="text-lg">🔎</span> Buscar por ID
            </button>
            {searchVentaId && (
              <button
                type="button"
                onClick={onClearVentaId}
                className="ml-2 text-blue-600 hover:text-blue-800 bg-blue-100 hover:bg-blue-200 rounded-full w-7 h-7 flex items-center justify-center transition-all duration-200 text-base font-bold"
                title="Limpiar filtro de ID"
              >
                ✕
              </button>
            )}
          </form>
        </div>
      )}

      {/* Validación de errores */}
      {!validation.valid && (
        <div className="mt-4 p-3 bg-orange-50 rounded-lg border border-orange-200">
          <p className="text-sm text-orange-700">
            <span className="font-medium">⚠️ Advertencia:</span> {validation.error}
          </p>
        </div>
      )}

      {/* Indicador de búsqueda automática */}
      {isSearching && (
        <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-600"></div>
            <p className="text-sm text-yellow-700">
              <span className="font-medium">🔍 Buscando ventas...</span>
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateFilters;
